import Phaser from "phaser";
import type { WorldState, TriggeredEvent, BattlePayload, BattleReward, RewardPayload, VotePayload, CutsceneSlide, RewardEffect } from "@/core";
import { applyRewardEffect } from "@/core";
import { levelUpSteps, cardChoiceStep, cardChoiceStepFromSpec, cardsByClass, commonCards, recomputeHeroDerived, type ClassId, type DomainId, type ChoiceKind, type ChoiceStep, type ReputationUpgradeId, type Stat } from "@/core/roscha";
import type { ActiveVote } from "../transport/EventTypes";
import { SimpleEmitter } from "./emitter";
import { floatingFromEffect } from "../scenes/floatingRewardMap";
import type { GameTransport, MoveResult } from "../transport/GameTransport";

export interface BattleCtx { payload: BattlePayload; eventId?: string }
export interface VoteCtx { vote: ActiveVote; eventId?: string }

export type AppMode =
  | "boot"
  | "map"
  | "town"
  | "characterCreator"
  | "battle"
  | "event"
  | "vote"
  | "cutscene"
  | "forge"
  | "tavern"
  | "guild"
  | "temple"
  | "equipment"
  | "choice";

export const REGISTRY_KEYS = {
  currentMode: "cdm.currentMode",
  currentEvent: "cdm.currentEvent",
  currentBattle: "cdm.currentBattle",
  currentVote: "cdm.currentVote",
  currentCutscene: "cdm.currentCutscene",
  currentChoice: "cdm.currentChoice",
  currentCharacterCreator: "cdm.currentCharacterCreator",
} as const;

function hasDisplayContent(p: RewardPayload): boolean {
  return !!((p.text && p.text.trim()) || p.image || p.reward);
}

export interface ChoiceCtx {
  steps: ChoiceStep[];
  onPick: (choice: ChoiceKind) => void;
  onDone: () => void;
}
export interface CharCreatorCtx { onChosen: (c: ClassId, d: DomainId) => void }

export type RegistryShape = {
  [REGISTRY_KEYS.currentMode]: AppMode;
  [REGISTRY_KEYS.currentEvent]: RewardPayload | null;
  [REGISTRY_KEYS.currentBattle]: BattleCtx | null;
  [REGISTRY_KEYS.currentVote]: VoteCtx | null;
  [REGISTRY_KEYS.currentCutscene]: CutsceneSlide[] | null;
  [REGISTRY_KEYS.currentChoice]: ChoiceCtx | null;
  [REGISTRY_KEYS.currentCharacterCreator]: CharCreatorCtx | null;
};



function enrichHeroState(state: WorldState): void {
  const h = state.hero as Partial<WorldState["hero"]> & WorldState["hero"];
  if (h.classId === undefined) h.classId = null;
  if (h.domainId === undefined) h.domainId = null;
  const defaultStats = { strength: 1, dexterity: 1, intelligence: 1, wisdom: 1, constitution: 1 };
  if (!h.baseStats) h.baseStats = { ...defaultStats };
  if (!h.stats) h.stats = { ...h.baseStats };
  if (typeof h.hp !== "number") h.hp = 30;
  if (typeof h.maxHp !== "number") h.maxHp = 30;
  if (typeof h.armor !== "number") h.armor = 2;
  if (typeof h.evasion !== "number") h.evasion = 11;
  if (!h.equipment) h.equipment = { head: null, body: null, hands: null, weapon: null, accessory: null };
  if (typeof h.fearAccumulated !== "number") h.fearAccumulated = 0;
  if (typeof h.xp !== "number") h.xp = 0;
  if (typeof h.level !== "number") h.level = 1;
  if (typeof h.reputation !== "number") h.reputation = 0;
  if (!h.reputationUpgrades) h.reputationUpgrades = {};
  if (!h.difficulty) h.difficulty = "normal";
  const validDeck = h.deck?.filter((id) => /^(basic_|cutting_|battle_|iron_|crushing_|blood_|word_|ray_|holy_|shadow_|curse|void_|thorn_|wild_|bark_|fire_|flame_|mana_|ice_|frost_|cloak_|nimble_)/.test(id)) ?? [];
  if (validDeck.length < 3) h.deck = [];
  if (h.classId && h.domainId) recomputeHeroDerived(state.hero);
}

export type AppSceneKey = "MapScene" | "TownScene" | "BattleScene" | "EventScene" | "CutsceneScene" | "VotingOverlay" | "ChoiceOverlay" | "CharacterCreatorScene" | "EquipmentOverlay" | "ForgeOverlay" | "TavernOverlay" | "GuildOverlay" | "TempleOverlay";

export interface FloatingRewardEvent {
  icon: string;
  iconTexture?: string;
  label: string;
  color?: string;
  tileX?: number;
  tileY?: number;
}

type Events = {
  worldState: WorldState;
  triggeredEvent: TriggeredEvent;
  energyUpdate: number;
  error: { message: string; details?: unknown };
  floatingReward: FloatingRewardEvent;
};

export class GameApp {
  private emitter = new SimpleEmitter<Events>();
  private transport: GameTransport;
  private worldState: WorldState | null = null;
  private unsubWorld?: () => void;
  private unsubTriggered?: () => void;
  private unsubEnergy?: () => void;
  private movementLocked = false;
  private eventQueue: TriggeredEvent[] = [];
  private processingEvent = false;
  private game?: Phaser.Game;

  constructor(transport: GameTransport) {
    this.transport = transport;
  }

  attachGame(game: Phaser.Game) {
    this.game = game;
    game.registry.set(REGISTRY_KEYS.currentMode, "boot");
    game.registry.set(REGISTRY_KEYS.currentEvent, null);
    game.registry.set(REGISTRY_KEYS.currentBattle, null);
    game.registry.set(REGISTRY_KEYS.currentVote, null);
    game.registry.set(REGISTRY_KEYS.currentCutscene, null);
    game.registry.set(REGISTRY_KEYS.currentChoice, null);
    game.registry.set(REGISTRY_KEYS.currentCharacterCreator, null);
  }

  private setReg<K extends keyof RegistryShape>(key: K, value: RegistryShape[K]) {
    this.game?.registry.set(key as string, value);
  }

  private setMode(mode: AppMode) {
    if (this.getCurrentMode() === mode) return;
    this.setReg(REGISTRY_KEYS.currentMode, mode);
  }

  getCurrentMode(): AppMode {
    return (this.game?.registry.get(REGISTRY_KEYS.currentMode) as AppMode) ?? "boot";
  }

  start() {
    this.attachTransport(this.transport);
  }

  setTransport(next: GameTransport) {
    this.unsubWorld?.();
    this.unsubTriggered?.();
    this.unsubEnergy?.();
    this.unsubReward?.();
    this.unsubFloaterDirect?.();
    this.transport.stop();
    this.transport = next;
    this.attachTransport(next);
  }

  private unsubReward?: () => void;
  private unsubFloaterDirect?: () => void;

  private attachTransport(transport: GameTransport) {
    this.unsubWorld = transport.onWorldState((s) => {
      enrichHeroState(s);
      this.worldState = s;
      this.emitter.emit("worldState", s);
    });
    this.unsubTriggered = transport.onTriggeredEvent((e) => {
      this.emitTriggeredEvent(e);
    });
    if (transport.onEnergyUpdate) {
      this.unsubEnergy = transport.onEnergyUpdate((energy) => {
        this.emitter.emit("energyUpdate", energy);
      });
    }
    if (transport.onRewardApplied) {
      this.unsubReward = transport.onRewardApplied((effect) => {
        const floater = floatingFromEffect(effect);
        if (floater) this.emitFloatingReward(floater);
      });
    }
    if (transport.onFloatingReward) {
      this.unsubFloaterDirect = transport.onFloatingReward((f) => {
        this.emitFloatingReward(f);
      });
    }
    transport.start();
  }

  getTransport(): GameTransport {
    return this.transport;
  }

  onWorldState(cb: (s: WorldState) => void) {
    return this.emitter.on("worldState", cb);
  }

  onTriggeredEvent(cb: (e: TriggeredEvent) => void) {
    return this.emitter.on("triggeredEvent", cb);
  }

  onEnergyUpdate(cb: (energy: number) => void) {
    return this.emitter.on("energyUpdate", cb);
  }

  onFloatingReward(cb: (r: FloatingRewardEvent) => void) {
    return this.emitter.on("floatingReward", cb);
  }

  emitFloatingReward(r: FloatingRewardEvent) {
    this.emitter.emit("floatingReward", r);
  }

  emitTriggeredEvent(event: TriggeredEvent) {
    this.eventQueue.push(event);
    if (!this.processingEvent && !this.movementLocked) {
      this.advanceToNext();
    }
  }

  private advanceToNext() {
    const event = this.eventQueue.shift();
    if (!event) {
      this.processingEvent = false;
      this.setMode("map");
      return;
    }
    this.processingEvent = true;
    this.emitter.emit("triggeredEvent", event);

    if (event.payload.kind === "battle") {
      this.setReg(REGISTRY_KEYS.currentBattle, { payload: event.payload, eventId: event.eventId });
      this.setMode("battle");
    } else if (event.payload.kind === "reward") {
      const payload = event.payload;
      if (hasDisplayContent(payload)) {
        this.setReg(REGISTRY_KEYS.currentEvent, payload);
        this.setMode("event");
      } else if (payload.cardPick) {
        this.openRewardCardPick(payload);
      } else {
        this.advanceToNext();
      }
    } else if (event.payload.kind === "vote") {
      this.setReg(REGISTRY_KEYS.currentVote, {
        vote: {
          id: event.eventId || "",
          question: event.payload.question,
          options: event.payload.options.map((o) => ({
            id: o.id, label: o.label, consequences: o.consequences || [],
          })),
          myChoice: null,
        },
        eventId: event.eventId,
      });
      this.setMode("vote");
    } else if ((event.payload as { kind: string }).kind === "level_up") {
      const hero = this.worldState?.hero;
      if (!hero || !hero.classId || !hero.domainId) {
        this.advanceToNext();
        return;
      }
      const domainPool = [...cardsByClass(hero.classId), ...commonCards()];
      this.openChoice(
        levelUpSteps(1, domainPool),
        (choice) => this.applyLevelUpChoice(choice),
        () => { this.advanceHeroLevel(); this.completeCurrentEvent(); },
      );
    } else {
      console.warn("[GameApp] unknown triggered event kind, draining queue:", (event.payload as { kind?: string }).kind);
      this.completeCurrentEvent();
    }
  }

  completeCurrentEvent() {
    if (this.eventQueue.length > 0) {
      this.advanceToNext();
      return;
    }
    this.processingEvent = false;
    this.movementLocked = false;
    this.setMode("map");
  }

  getWorldState() {
    return this.worldState;
  }

  emitWorldState() {
    if (this.worldState) this.emitter.emit("worldState", this.worldState);
  }

  getEnergy(): number {
    return this.transport.getEnergy();
  }

  setMovementLocked(locked: boolean) {
    this.movementLocked = locked;
    if (!locked && !this.processingEvent && this.eventQueue.length > 0) {
      this.advanceToNext();
    }
  }

  applyRewardEffect(effect: RewardEffect) {
    if (!this.worldState) return;
    applyRewardEffect(this.worldState, effect);
    this.emitter.emit("worldState", this.worldState);
  }

  isMovementLocked() {
    return this.movementLocked;
  }

  async move(x: number, y: number): Promise<MoveResult> {
    if (this.movementLocked) return { ok: false, reason: "Movement locked" };
    this.movementLocked = true;
    try {
      const res = await this.transport.move(x, y);
      if (!res.ok) this.movementLocked = false;
      return res;
    } catch (e) {
      this.movementLocked = false;
      const msg = e instanceof Error ? e.message : "move failed";
      this.emitter.emit("error", { message: msg, details: e });
      return { ok: false, reason: msg };
    }
  }

  openTown() {
    if (this.movementLocked) return;
    this.setMode("town");
  }

  async buildInTown(townId: string, buildingId: string): Promise<boolean> {
    const transport = this.getTransport();
    if (!transport.buildInTown) return false;
    return transport.buildInTown(townId, buildingId);
  }

  resolveBattle(reward: BattleReward) {
    this.transport.resolveBattle?.(reward);
    const levels = this.transport.applyXp?.(reward.xpGained) ?? 0;
    const hero = this.worldState?.hero;
    const domainPool = hero?.classId ? [...cardsByClass(hero.classId), ...commonCards()] : [...commonCards()];
    const lootStep = reward.cardChoicePool?.length ? cardChoiceStep(reward.cardChoicePool) : null;
    const adminStep = reward.cardPick ? cardChoiceStepFromSpec(reward.cardPick, domainPool) : null;
    const steps: ChoiceStep[] = [
      ...levelUpSteps(levels, domainPool),
      ...(lootStep ? [lootStep] : []),
      ...(adminStep ? [adminStep] : []),
    ];
    if (steps.length === 0) {
      this.completeCurrentEvent();
      return;
    }
    this.openChoice(steps, (c) => this.applyLevelUpChoice(c), () => this.completeCurrentEvent());
  }

  
  finishRewardEvent() {
    const payload = this.game?.registry.get(REGISTRY_KEYS.currentEvent) as RewardPayload | null;
    if (payload?.cardPick) {
      this.openRewardCardPick(payload);
    } else {
      this.completeCurrentEvent();
    }
  }

  private openRewardCardPick(payload: RewardPayload) {
    const hero = this.worldState?.hero;
    const domainPool = hero?.classId ? [...cardsByClass(hero.classId), ...commonCards()] : [...commonCards()];
    const step = payload.cardPick ? cardChoiceStepFromSpec(payload.cardPick, domainPool) : null;
    if (!step) {
      this.completeCurrentEvent();
      return;
    }
    this.openChoice([step], (c) => this.applyLevelUpChoice(c), () => this.completeCurrentEvent());
  }

  openChoice(steps: ChoiceStep[], onPick: (choice: ChoiceKind) => void, onDone: () => void) {
    this.setReg(REGISTRY_KEYS.currentChoice, { steps, onPick, onDone });
    this.setMode("choice");
  }

  applyLevelUpChoice(choice: ChoiceKind) {
    this.transport.applyLevelUpChoice?.(choice);
  }

  advanceHeroLevel() {
    this.transport.advanceHeroLevel?.();
  }


  openCharacterCreator(onChosen: (classId: ClassId, domainId: DomainId) => void) {
    this.setReg(REGISTRY_KEYS.currentCharacterCreator, { onChosen });
    this.setMode("characterCreator");
  }

  
  applyCharacterCreation(classId: ClassId, domainId: DomainId) {
    this.transport.applyCharacterCreation?.(classId, domainId);
  }

  equipItem(itemId: string) {
    this.transport.equipItem?.(itemId);
  }

  unequipSlot(slot: import("@/core/roscha").EquipmentSlot) {
    this.transport.unequipSlot?.(slot);
  }

  
  openEquipment() {
    if (this.movementLocked) return;
    this.setMode("equipment");
  }

  openForge()   { this.setMode("forge"); }
  openTavern()  { this.setMode("tavern"); }
  openGuild()   { this.setMode("guild"); }
  openTemple()  { this.setMode("temple"); }

  buyTavernCard(cardId: string, goldCost: number) { this.transport.buyTavernCard?.(cardId, goldCost); }
  restoreTemple(kind: "hp" | "energy") { return this.transport.restoreTemple?.(kind); }
  applyReputationUpgrade(id: ReputationUpgradeId) { this.transport.applyReputationUpgrade?.(id); }
  resetFear(goldCost: number) { this.transport.resetFear?.(goldCost); }
  applyTempleBuff(stat: Stat, goldCost: number) { this.transport.applyTempleBuff?.(stat, goldCost); }
  upgradeDeckCard(cardId: string, goldCost: number) { this.transport.upgradeDeckCard?.(cardId, goldCost); }
  setActiveDeck(deck: string[]) { this.transport.setActiveDeck?.(deck); }

  
  async resetSaveAndReload() {
    try {
      await this.transport.resetWorld?.();
    } catch (e) {
      console.warn("[GameApp] reset world failed:", e);
    }
    window.location.reload();
  }

  openBattle(battle: BattlePayload) {
    this.processingEvent = true;
    this.setReg(REGISTRY_KEYS.currentBattle, { payload: battle });
    this.setMode("battle");
  }

  openEvent(event: RewardPayload) {
    this.processingEvent = true;
    this.setReg(REGISTRY_KEYS.currentEvent, event);
    this.setMode("event");
  }

  openVote(vote: VotePayload, mapObjectId?: string) {
    this.processingEvent = true;
    this.setReg(REGISTRY_KEYS.currentVote, {
      vote: {
        id: mapObjectId || "",
        question: vote.question,
        options: vote.options.map((o) => ({
          id: o.id, label: o.label, consequences: o.consequences || [],
        })),
        myChoice: null,
      },
      eventId: mapObjectId,
    });
    this.setMode("vote");
  }

  openCutscene(slides: CutsceneSlide[]) {
    this.processingEvent = true;
    this.setReg(REGISTRY_KEYS.currentCutscene, slides);
    this.setMode("cutscene");
  }

  closeCutscene() {
    this.backToMap();
  }

  backToMap() {
    this.processingEvent = false;
    this.movementLocked = false;
    if (this.eventQueue.length > 0) {
      this.advanceToNext();
      return;
    }
    this.setMode("map");
  }
}
